import { ErrorTracker } from '@futrli/shared-services';
import React from 'react';

import { ERROR_STATUS } from '../core/constants';
import { AnyError } from '../core/types';
import { getErrorStatus } from '../utils/utils';
import { shouldRaiseErrorToErrorBoundary } from '../utils/with-error-boundary-utils';

export interface WithErrorBoundaryProps {
  onManageError: (error: AnyError, ignoreErrors?: ERROR_STATUS[]) => void;
}

export interface State {
  error: AnyError | null;
}

/**
 * HOC which propagates the error to the main error boundary component.
 *
 * NOTE: This method will be used when an error happens in an event handler or async method
 * because if the error is thrown in a lifecicle method, then it is raised automatically
 * by React.
 * @see https://reactjs.org/docs/error-boundaries.html#how-about-event-handlers
 *
 * @template P - Component props.
 *
 * @param {React.ComponentType<P>} Component - Component to wrap.
 * @returns {React.ComponentType<Props>} - Wrapped component with the error functionality added.
 */
export function withErrorBoundary<P extends WithErrorBoundaryProps>(Component: React.ComponentType<P>) {

  type Props = Omit<P, keyof WithErrorBoundaryProps>;

  return class ErrorBoundaryComponent extends React.Component<Props, State> {
    public state: Readonly<State> = {
      error: null
    };

    public render(): JSX.Element {
      const { error } = this.state;

      if (error) {
        throw error;
      }

      /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
      return (<Component {...this.props as any} onManageError={this.handleManageError} />);
    }

    /**
     * Manage the error passed as a parameter. If the error is an specified error to be rendered as an error page
     * component, then raise it to ErrorBoundary component, if not, log the error in Sentry.
     * In case we want to avoid some error codes, they can be passed as an array.
     *
     * @param {AnyError | null} error
     * @param {ERROR_STATUS[] | []} ignoreErrors
     */
    public handleManageError = (error: AnyError | null, ignoreErrors: ERROR_STATUS[] = []) => {
      const errorStatus = getErrorStatus(error);

      if (errorStatus && ignoreErrors.includes(errorStatus)) {
        return;
      }

      if (shouldRaiseErrorToErrorBoundary(error)) {
        this.setState({ error });
      } else {
        ErrorTracker.captureException(error);
      }
    }
  };
}
